import { Dispatch } from 'react';
import { Params } from 'ahooks/lib/useAntdTable/types';
import { Tooltip } from 'antd';
import { RawAxiosRequestHeaders } from 'axios';
import dayjs from 'dayjs';
import { lowerCase, startCase } from 'lodash';

import { apiGet } from 'api';

import { Status } from 'components';

import { addQueryParamsToURL, getDateForPayload, getTableQueryParams } from 'utils';

import { Links } from 'links';

import { MatchedBy, OfferLineItemMatch, OfferMatchesResponse } from 'codegen';
import { TColumn, TFieldProps } from 'types';

export const columns: TColumn<OfferLineItemMatch>[] = [{
  width: 200,
  title: 'Line Item',
  dataIndex: 'lineItem',
  render: (value, record) => {
    if (record.status === OfferLineItemMatch.status.MANUAL_MATCH) {
      return (
        <Tooltip placement='bottomLeft' title={`${value.name} - ${value.sku}`}>
          <p className='truncate'>
            {`${value.name} - ${value.sku}`}
          </p>
        </Tooltip>
      );
    }

    if (record.matchedBy?.type === MatchedBy.type.SKU) {
      return value.sku;
    }

    return (
      <Tooltip placement='bottomLeft' title={value.name}>
        <p className='truncate'>
          {value.name}
        </p>
      </Tooltip>
    );
  },
}, {
  width: 200,
  title: 'Matched By',
  dataIndex: 'matchedBy',
  render: (matchedBy) => matchedBy && `${matchedBy.type} ${matchedBy.value}`,
}, {
  type: TColumn.type.Status,
  width: 150,
  title: 'Status',
  dataIndex: 'status',
}, {
  type: TColumn.type.Percent,
  width: 150,
  title: 'Matching Score',
  dataIndex: 'matchScore',
}, {
  type: TColumn.type.Percent,
  width: 150,
  title: 'OCR Score',
  dataIndex: 'ocrScore',
}, {
  type: TColumn.type.IDRedirect,
  width: 100,
  title: 'Snap ID',
  dataIndex: 'snapId',
  pathname: Links.Snaps,
}, {
  type: TColumn.type.Date,
  width: 200,
  title: 'Created Date (EST)',
  dataIndex: 'createdAt',
}, {
  width: 160,
  title: 'Collector ID',
  dataIndex: 'collectorId',
}, {
  type: TColumn.type.Status,
  width: 150,
  title: 'Issuance Status',
  dataIndex: 'issuanceStatus',
}, {
  width: 150,
  title: 'Retailer',
  dataIndex: 'retailer',
}];

export const fields = (isAdmin: boolean): TFieldProps[] => [{
  type: TFieldProps.type.DateRangePicker,
  name: 'submittedDates',
  colProps: { xs: 8, xxl: 4 },
  placeholder: 'Match Created Date',
}, {
  type: TFieldProps.type.Select,
  name: 'matchStatuses',
  mode: 'multiple',
  options: Object.values(OfferLineItemMatch.status).map((st) => ({ label: startCase(lowerCase(st)), value: st })),
  colProps: { xs: 8, xxl: 3 },
  placeholder: 'Match Status',
  tagRender: ({ value }) => <Status key={value} status={value} />,
  maxTagCount: 'responsive',
}, ...(isAdmin
  ? [{
    type: TFieldProps.type.Text,
    name: 'collectorNumber',
    colProps: { xs: 4, xxl: 3 },
    placeholder: 'Collector Number',
  }]
  : []
), {
  type: TFieldProps.type.Text,
  name: 'collectorId',
  colProps: { xs: !isAdmin ? 8 : 4, xxl: 3 },
  placeholder: 'Collector ID',
}];

interface IInitialValues {
  matchStatuses: string[];
  collectorId?: string;
  submittedDates: dayjs.Dayjs[] | null;
  collectorNumber: string;
}

export const handleOfferMatchesTableChange = (
  headers: RawAxiosRequestHeaders,
  setUrl: Dispatch<string>,
  offerId?: string,
) => async (
  params: Params[0],
  {
    matchStatuses,
    collectorId,
    submittedDates,
    collectorNumber,
  }: IInitialValues,
) => {
  const { sort, ...otherParams } = getTableQueryParams(params);

  const mainParams = {
    ...(matchStatuses && { matchStatuses }),
    ...(collectorId && { collectorId }),
    ...(collectorNumber && { collectorNumber }),
    ...(submittedDates && {
      createdTo: getDateForPayload(dayjs(submittedDates[1]).endOf('day')),
      createdFrom: getDateForPayload(dayjs(submittedDates[0]).startOf('day')),
    }),
  };

  const queryParams = {
    ...otherParams,
    ...mainParams,
  };

  const url = addQueryParamsToURL(`offers/details/${offerId}/matches`, queryParams);

  const csvURL = addQueryParamsToURL(`offers/details/${offerId}/matches/export`, mainParams);

  setUrl(csvURL);

  const { data } = await apiGet<OfferMatchesResponse>({ url, headers });

  return {
    list: data.matches,
    total: data.total,
  };
};
